import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  startTransition,
} from "react";
import { supabase } from "lib/supabaseClient";
import { insertLog } from "services/logService";
import Card from "components/card";
import {
  MdCheckCircle,
  MdCancel,
  MdDelete,
  MdSearch,
  MdCloudDownload,
  MdAttachFile,
  MdPrint,
} from "react-icons/md";

const getAvatarCacheKey = (url) =>
  url ? `core_avatar_cache_${btoa(url).slice(0, 120)}` : null;

const readCachedAvatar = (url) => {
  const key = getAvatarCacheKey(url);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeCachedAvatar = (url, dataUrl) => {
  const key = getAvatarCacheKey(url);
  if (!key || !dataUrl) return;
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    try {
      sessionStorage.setItem(key, dataUrl);
    } catch {}
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });

const fetchAvatarBlob = async (url) => {
  if (!url) return null;
  try {
    if (url.includes("supabase.co/storage/v1/object/public/")) {
      const path = url.split("/public/")[1];
      const bucket = path.split("/")[0];
      const filePath = path.split("/").slice(1).join("/");
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(filePath);
      if (!error && data) return data;
    }
    const response = await fetch(url, { cache: "force-cache" });
    return await response.blob();
  } catch {
    return null;
  }
};

const SafeAvatar = React.memo(({ url, wrapperClassName, imageClassName }) => {
  const [cachedUrl, setCachedUrl] = useState(() => readCachedAvatar(url));
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    setCachedUrl(readCachedAvatar(url));
  }, [url]);

  useEffect(() => {
    let isActive = true;
    let nextObjectUrl;
    const load = async () => {
      if (!url) {
        setObjectUrl(null);
        return;
      }
      const blob = await fetchAvatarBlob(url);
      if (!isActive) return;
      if (blob) {
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl && isActive) {
          writeCachedAvatar(url, dataUrl);
          setCachedUrl(dataUrl);
        }
      }
    };
    load();
    return () => {
      isActive = false;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [url]);

  const displayUrl =
    objectUrl || cachedUrl || "https://i.ibb.co/mRpg6P0/default-avatar.png";
  return (
    <div
      className={`relative select-none ${wrapperClassName}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 z-10 bg-transparent" />
      <div
        className={imageClassName}
        style={{
          backgroundImage: `url(${displayUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          pointerEvents: "none",
        }}
      />
    </div>
  );
});

const LeaveRequestsManagement = () => {
  const [requests, setRequests] = useState(() => {
    const ssc = sessionStorage.getItem("core_requests_cache");
    if (ssc) return JSON.parse(ssc);
    const lsc = localStorage.getItem("core_requests_cache");
    return lsc ? JSON.parse(lsc) : [];
  });
  const [loading, setLoading] = useState(!requests.length);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const isMounted = useRef(true);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    archived: 0,
    all: 0,
  });
  const statsTimerRef = useRef(null);

  const cardStyle = "bg-white border border-gray-200 shadow-sm";

  useEffect(() => {
    isMounted.current = true;
    const getCurrentAdmin = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user && isMounted.current) {
        const { data } = await supabase
          .from("employees")
          .select("full_name, employee_number, role")
          .eq("id", user.id)
          .single();
        setCurrentAdmin(data);
      }
    };
    getCurrentAdmin();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      let query = supabase
        .from("employee_leaves")
        .select(
          "*, employee:employees(id, full_name, employee_number, job_title, department, avatar_url)",
          { count: "exact" }
        )
        .order("created_at", { ascending: false });
      if (filterStatus !== "all") {
        query = query.eq("status", filterStatus);
      }
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error } = await query.range(from, to);
      if (error) throw error;
      if (isMounted.current) {
        let list = data || [];
        const now = new Date();
        const idsToArchive = list
          .filter(
            (r) =>
              r.status === "approved" &&
              r.approved_at &&
              (now - new Date(r.approved_at)) / (1000 * 60 * 60 * 24) > 7
          )
          .map((r) => r.id);
        if (idsToArchive.length) {
          await supabase
            .from("employee_leaves")
            .update({ status: "archived" })
            .in("id", idsToArchive);
          list = list.map((r) =>
            idsToArchive.includes(r.id) ? { ...r, status: "archived" } : r
          );
        }
        const mergedLocal = page === 0 ? list : null;
        startTransition(() =>
          setRequests((prev) => {
            if (page === 0) return list;
            const map = new Map();
            [...prev, ...list].forEach((r) => map.set(r.id, r));
            return Array.from(map.values());
          })
        );
        setHasMore((data || []).length === pageSize);
        if (page === 0) {
          try {
            if (Array.isArray(mergedLocal)) {
              sessionStorage.setItem(
                "core_requests_cache",
                JSON.stringify(mergedLocal)
              );
              localStorage.setItem(
                "core_requests_cache",
                JSON.stringify(mergedLocal)
              );
              sessionStorage.setItem(
                `core_requests_cache_${filterStatus}`,
                JSON.stringify(mergedLocal)
              );
            }
          } catch {}
        }
        setLoading(false);
      }
    } catch (error) {
      console.error("Fetch Interrupted");
    }
  }, [filterStatus, page, pageSize]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setSearchTerm("");
  }, [filterStatus]);
  useEffect(() => {
    setPage(0);
    setHasMore(true);
  }, [filterStatus]);

  useEffect(() => {
    const cached = sessionStorage.getItem("core_requests_stats");
    if (cached) {
      try {
        startTransition(() => setStats(JSON.parse(cached)));
      } catch {}
    }
  }, []);

  useEffect(() => {
    const key =
      filterStatus === "all"
        ? "core_requests_cache_all"
        : `core_requests_cache_${filterStatus}`;
    const cached = sessionStorage.getItem(key);
    if (cached) {
      try {
        const list = JSON.parse(cached);
        startTransition(() => setRequests(Array.isArray(list) ? list : []));
        setLoading(false);
      } catch {}
    }
  }, [filterStatus]);

  const refreshStats = useCallback(async () => {
    try {
      const [p, a, r, ar, all] = await Promise.all([
        supabase
          .from("employee_leaves")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
        supabase
          .from("employee_leaves")
          .select("id", { count: "exact", head: true })
          .eq("status", "approved"),
        supabase
          .from("employee_leaves")
          .select("id", { count: "exact", head: true })
          .eq("status", "rejected"),
        supabase
          .from("employee_leaves")
          .select("id", { count: "exact", head: true })
          .eq("status", "archived"),
        supabase
          .from("employee_leaves")
          .select("id", { count: "exact", head: true }),
      ]);
      if (!isMounted.current) return;
      const nextStats = {
        pending: p.count || 0,
        approved: a.count || 0,
        rejected: r.count || 0,
        archived: ar.count || 0,
        all: all.count || 0,
      };
      startTransition(() => setStats(nextStats));
      try {
        sessionStorage.setItem(
          "core_requests_stats",
          JSON.stringify(nextStats)
        );
      } catch {}
    } catch {}
  }, []);

  const refreshStatsDebounced = useCallback(() => {
    if (statsTimerRef.current) clearTimeout(statsTimerRef.current);
    statsTimerRef.current = setTimeout(() => {
      refreshStats();
    }, 300);
  }, [refreshStats]);

  useEffect(() => {
    refreshStatsDebounced();
  }, [refreshStatsDebounced]);

  useEffect(() => {
    const enrich = async (row) => {
      const { data: emp } = await supabase
        .from("employees")
        .select(
          "id, full_name, employee_number, job_title, department, avatar_url"
        )
        .eq("id", row.employee_id)
        .single();
      return { ...row, employee: emp || row.employee };
    };
    const ch = supabase
      .channel("core_requests_rt")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "employee_leaves" },
        async (payload) => {
          const row = payload.new;
          if (filterStatus === "all" || row.status === filterStatus) {
            const enriched = await enrich(row);
            startTransition(() => setRequests((prev) => [enriched, ...prev]));
          }
          try {
            const key = `core_requests_cache_${row.status}`;
            const arr = JSON.parse(sessionStorage.getItem(key) || "[]");
            const map = new Map(arr.map((r) => [r.id, r]));
            const enriched = await enrich(row);
            map.set(row.id, enriched);
            const next = Array.from(map.values());
            sessionStorage.setItem(key, JSON.stringify(next));
            const allMap = new Map();
            ["pending", "approved", "rejected", "archived"].forEach((s) => {
              (
                JSON.parse(
                  sessionStorage.getItem(`core_requests_cache_${s}`) || "[]"
                ) || []
              ).forEach((r) => allMap.set(r.id, r));
            });
            sessionStorage.setItem(
              "core_requests_cache_all",
              JSON.stringify(
                Array.from(allMap.values()).sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                )
              )
            );
          } catch {}
          refreshStatsDebounced();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "employee_leaves" },
        async (payload) => {
          const row = payload.new;
          const old = payload.old || {};
          const enriched = await enrich(row);
          startTransition(() =>
            setRequests((prev) => {
              const next = prev.map((r) =>
                r.id === enriched.id
                  ? {
                      ...r,
                      ...enriched,
                      employee: r.employee || enriched.employee,
                    }
                  : r
              );
              if (filterStatus !== "all")
                return next.filter((r) => r.status === filterStatus);
              return next;
            })
          );
          try {
            const oldKey = `core_requests_cache_${old.status}`;
            const newKey = `core_requests_cache_${row.status}`;
            if (old.status && oldKey !== newKey) {
              const oldArr = JSON.parse(
                sessionStorage.getItem(oldKey) || "[]"
              ).filter((r) => r.id !== row.id);
              sessionStorage.setItem(oldKey, JSON.stringify(oldArr));
            }
            const newArr = JSON.parse(sessionStorage.getItem(newKey) || "[]");
            const map = new Map(newArr.map((r) => [r.id, r]));
            map.set(row.id, enriched);
            sessionStorage.setItem(
              newKey,
              JSON.stringify(Array.from(map.values()))
            );
            const allMap = new Map();
            ["pending", "approved", "rejected", "archived"].forEach((s) => {
              (
                JSON.parse(
                  sessionStorage.getItem(`core_requests_cache_${s}`) || "[]"
                ) || []
              ).forEach((r) => allMap.set(r.id, r));
            });
            sessionStorage.setItem(
              "core_requests_cache_all",
              JSON.stringify(
                Array.from(allMap.values()).sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                )
              )
            );
          } catch {}
          refreshStatsDebounced();
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "employee_leaves" },
        (payload) => {
          const old = payload.old;
          startTransition(() =>
            setRequests((prev) => prev.filter((r) => r.id !== old.id))
          );
          try {
            const key = `core_requests_cache_${old.status}`;
            const arr = JSON.parse(sessionStorage.getItem(key) || "[]").filter(
              (r) => r.id !== old.id
            );
            sessionStorage.setItem(key, JSON.stringify(arr));
            const allMap = new Map();
            ["pending", "approved", "rejected", "archived"].forEach((s) => {
              (
                JSON.parse(
                  sessionStorage.getItem(`core_requests_cache_${s}`) || "[]"
                ) || []
              ).forEach((r) => allMap.set(r.id, r));
            });
            sessionStorage.setItem(
              "core_requests_cache_all",
              JSON.stringify(
                Array.from(allMap.values()).sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                )
              )
            );
          } catch {}
          refreshStatsDebounced();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [filterStatus, refreshStatsDebounced]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "00/00/0000";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? "00/00/0000"
      : `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
  };
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "00/00/0000 00:00";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "00/00/0000 00:00";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason("");
    setShowModal(true);
  };

  // ✅ حذف طلب إجازة (سريع)
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this leave request permanently?")) return;

    // Optimistic Update: حذف فوري من الواجهة
    const oldRequests = [...requests];
    const targetReq = requests.find((r) => r.id === id);
    startTransition(() =>
      setRequests((prev) => prev.filter((r) => r.id !== id))
    );

    try {
      // If approved, restore balance and reset status
      if (targetReq && targetReq.status === "approved") {
        const { data: emp } = await supabase
          .from("employees")
          .select(
            "annual_leave_balance, annual_leave_used, sick_leave_count, emergency_leave_count"
          )
          .eq("id", targetReq.employee_id)
          .single();

        const updates = {};
        if (targetReq.leave_type === "Annual Leave") {
          updates.annual_leave_balance =
            (emp.annual_leave_balance || 0) + targetReq.days_count;
          updates.annual_leave_used = Math.max(
            0,
            (emp.annual_leave_used || 0) - targetReq.days_count
          );
        } else if (targetReq.leave_type === "Sick Leave") {
          updates.sick_leave_count = Math.max(
            0,
            (emp.sick_leave_count || 0) - targetReq.days_count
          );
        } else if (targetReq.leave_type === "Emergency Leave") {
          updates.emergency_leave_count = Math.max(
            0,
            (emp.emergency_leave_count || 0) - targetReq.days_count
          );
        }

        // Reset status to Active
        updates.status = "Active";
        updates.is_on_leave = false;

        if (Object.keys(updates).length > 0) {
          await supabase
            .from("employees")
            .update(updates)
            .eq("id", targetReq.employee_id);
        }
      }

      const { error } = await supabase
        .from("employee_leaves")
        .delete()
        .eq("id", id);
      if (error) throw error;

      await insertLog({
        timestamp: new Date().toISOString(),
        actor_name: currentAdmin?.full_name || "Admin",
        actor_number: currentAdmin?.employee_number || "—",
        actor_role: currentAdmin?.role || "admin",
        target_id: targetReq?.employee_id || null,
        target_name: targetReq?.employee?.full_name || "Employee",
        target_number: targetReq?.employee?.employee_number || "—",
        action: "LEAVE_DELETED",
        details: `Deleted ${targetReq?.leave_type} request [${targetReq?.days_count} days]`,
      });

      // تم الحذف — بدون alert ثقيل
    } catch (e) {
      setRequests(oldRequests); // تراجع عن الحذف لو فشل
      alert("Delete Failed: " + e.message);
    } finally {
      refreshStatsDebounced();
    }
  };

  // ✅ قبول طلب (سريع جداً)
  const handleApprove = async (request) => {
    if (!currentAdmin) return alert("Verify Identity First!");
    setProcessingId(request.id);

    // تحديث فوري في الواجهة (Optimistic)
    startTransition(() =>
      setRequests((prev) =>
        prev.map((r) =>
          r.id === request.id
            ? {
                ...r,
                status: "approved",
                approved_by: currentAdmin.full_name,
                approver_number: currentAdmin.employee_number,
                approved_at: new Date().toISOString(),
              }
            : r
        )
      )
    );

    try {
      const { data: emp } = await supabase
        .from("employees")
        .select(
          "annual_leave_balance, annual_leave_used, sick_leave_count, emergency_leave_count"
        )
        .eq("id", request.employee_id)
        .single();

      const updates = {};
      // Calculate return date (end_date + 1 day)
      const returnDate = new Date(request.end_date);
      returnDate.setDate(returnDate.getDate() + 1);
      const toSQL = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      if (request.leave_type === "Annual Leave") {
        updates.annual_leave_balance =
          (emp.annual_leave_balance || 30) - request.days_count;
        updates.annual_leave_used =
          (emp.annual_leave_used || 0) + request.days_count;
      } else if (request.leave_type === "Sick Leave") {
        updates.sick_leave_count =
          (emp.sick_leave_count || 0) + request.days_count;
      } else if (request.leave_type === "Emergency Leave") {
        updates.emergency_leave_count =
          (emp.emergency_leave_count || 0) + request.days_count;
      }

      // Update status and return date
      updates.status = request.leave_type;
      updates.is_on_leave = true;
      updates.last_leave_end_date = toSQL(returnDate);

      if (Object.keys(updates).length > 0) {
        await supabase
          .from("employees")
          .update(updates)
          .eq("id", request.employee_id);
      }

      await supabase
        .from("employee_leaves")
        .update({
          status: "approved",
          approved_by: currentAdmin.full_name,
          approver_number: currentAdmin.employee_number,
          approved_at: new Date().toISOString(),
        })
        .eq("id", request.id);

      // الـ log والـ notification في الخلفية بدون انتظار
      insertLog({
        timestamp: new Date().toISOString(),
        actor_name: currentAdmin.full_name,
        actor_number: currentAdmin.employee_number,
        actor_role: currentAdmin.role || "admin",
        target_id: request.employee_id,
        target_name: request.employee?.full_name || "Employee",
        target_number: request.employee?.employee_number || "—",
        action: "LEAVE_APPROVED",
        details: `Approved ${request.leave_type} [${request.days_count} days] | Period: ${request.start_date} → ${request.end_date}`,
      }).catch(() => {});

      supabase.from("notifications").insert([
        {
          recipient_id: request.employee_id,
          type: "leave_approved",
          title: "AUTHORIZED",
          message: `Your ${request.leave_type} approved.`,
        },
      ]).catch(() => {});
    } catch (e) {
      fetchRequests(); // استعادة البيانات الأصلية في حال الخطأ
      alert(e.message);
    } finally {
      setProcessingId(null);
      refreshStatsDebounced();
    }
  };

  // ✅ رفض طلب (سريع)
  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;
    const reqId = selectedRequest.id;
    setProcessingId(reqId);
    setShowModal(false);

    // تحديث فوري
    startTransition(() =>
      setRequests((prev) =>
        prev.map((r) =>
          r.id === reqId
            ? {
                ...r,
                status: "rejected",
                rejection_reason: rejectionReason,
                approved_by: currentAdmin?.full_name,
                approver_number: currentAdmin?.employee_number,
                approved_at: new Date().toISOString(),
              }
            : r
        )
      )
    );

    try {
      await supabase
        .from("employee_leaves")
        .update({
          status: "rejected",
          rejection_reason: rejectionReason.trim(),
          approved_by: currentAdmin.full_name,
          approver_number: currentAdmin.employee_number,
          approved_at: new Date().toISOString(),
        })
        .eq("id", reqId);

      await insertLog({
        timestamp: new Date().toISOString(),
        actor_name: currentAdmin.full_name,
        actor_number: currentAdmin.employee_number,
        actor_role: currentAdmin.role || "admin",
        target_id: selectedRequest.employee_id,
        target_name: selectedRequest.employee?.full_name || "Employee",
        target_number: selectedRequest.employee?.employee_number || "—",
        action: "LEAVE_REJECTED",
        details: `Rejected ${selectedRequest.leave_type} [${selectedRequest.days_count} days] | Reason: ${rejectionReason}`,
      });

      await supabase.from("notifications").insert([
        {
          recipient_id: selectedRequest.employee_id,
          type: "leave_rejected",
          title: "REJECTED",
          message: `Reason: ${rejectionReason}`,
        },
      ]);
    } catch (e) {
      fetchRequests();
    } finally {
      setProcessingId(null);
      refreshStatsDebounced();
    }
  };

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      const matchesStatus =
        filterStatus === "all" || req.status === filterStatus;
      const term = searchTerm.toLowerCase();
      return (
        matchesStatus &&
        (req.employee?.full_name?.toLowerCase().includes(term) ||
          req.employee?.employee_number?.includes(term))
      );
    });
  }, [requests, filterStatus, searchTerm]);

  const handleForceReload = () => {
    setPage(0);
    setHasMore(true);
    setLoading(true);
    fetchRequests();
    refreshStatsDebounced();
  };

  const handleLoadMore = () => {
    if (!hasMore || loading) return;
    setPage((prev) => prev + 1);
  };

  const handleDeleteAll = async () => {
    const ok = window.confirm("⚠️ DELETE ALL IN CURRENT VIEW?");
    if (!ok) return;
    try {
      if (filterStatus === "all") {
        await supabase
          .from("employee_leaves")
          .delete()
          .in("status", [
            "pending",
            "approved",
            "rejected",
            "archived",
            "cancelled",
          ]);
        startTransition(() => setRequests([]));
        try {
          sessionStorage.setItem("core_requests_cache_all", JSON.stringify([]));
          ["pending", "approved", "rejected", "archived"].forEach((s) =>
            sessionStorage.setItem(
              `core_requests_cache_${s}`,
              JSON.stringify([])
            )
          );
        } catch {}
      } else {
        await supabase
          .from("employee_leaves")
          .delete()
          .eq("status", filterStatus);
        startTransition(() =>
          setRequests((prev) => prev.filter((r) => r.status !== filterStatus))
        );
        try {
          sessionStorage.setItem(
            `core_requests_cache_${filterStatus}`,
            JSON.stringify([])
          );
          const allMap = new Map();
          ["pending", "approved", "rejected", "archived"].forEach((s) => {
            (
              JSON.parse(
                sessionStorage.getItem(`core_requests_cache_${s}`) || "[]"
              ) || []
            ).forEach((r) => allMap.set(r.id, r));
          });
          sessionStorage.setItem(
            "core_requests_cache_all",
            JSON.stringify(
              Array.from(allMap.values()).sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              )
            )
          );
        } catch {}
      }
      refreshStatsDebounced();
      alert("✅ DELETED");
    } catch (e) {
      alert("❌ Delete Failed");
    }
  };

  return (
    <div className="animate-fadeIn mt-4 w-full space-y-6 p-4 md:p-6" dir="ltr">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="font-mono text-2xl uppercase italic text-core-charcoal md:text-3xl">
            LEAVE REQUESTS
          </h1>
          <p className="font-mono text-[11px] uppercase tracking-widest text-core-accent">
            Admin Authorization Portal
          </p>
        </div>
        <div className="flex gap-2">
          {[
            { l: "NEW", k: "pending", c: "text-yellow-600" },
            { l: "APPROVED", k: "approved", c: "text-emerald-600" },
            { l: "REJECTED", k: "rejected", c: "text-red-600" },
            { l: "ARCHIVE", k: "archived", c: "text-gray-500" },
            { l: "ALL", k: "all", c: "text-core-accent" },
          ].map((s) => (
            <div
              key={s.k}
              onClick={() => setFilterStatus(s.k)}
              className={`cursor-pointer rounded-2xl border px-5 py-2 transition-all ${
                filterStatus === s.k
                  ? "border-core-accent bg-white shadow-lg"
                  : "border-gray-200 bg-gray-50 opacity-70 hover:opacity-100"
              }`}
            >
              <p className={`text-lg ${s.c} font-mono leading-none`}>
                {stats[s.k]}
              </p>
              <span className="text-[9px] uppercase tracking-widest text-gray-500">
                {s.l}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Card extra={`p-4 ${cardStyle}`}>
        <div className="flex flex-col justify-between gap-4 md:flex-row">
          <div className="relative w-full md:w-80">
            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="SEARCH_ID_OR_NAME..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 font-mono text-[11px] text-core-charcoal outline-none focus:border-core-accent focus:bg-white transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleForceReload}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-[10px] uppercase text-gray-600 transition-all hover:bg-white hover:text-core-accent hover:border-core-accent"
            >
              <MdCloudDownload /> FORCE_RELOAD
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-[10px] uppercase text-gray-600 transition-all hover:bg-white hover:text-core-accent hover:border-core-accent"
            >
              <MdPrint /> PRINT
            </button>
            <button
              onClick={handleDeleteAll}
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[10px] uppercase text-red-500 transition-all hover:bg-red-600 hover:text-white"
            >
              DELETE_ALL
            </button>
          </div>
        </div>
      </Card>

      {loading && requests.length === 0 && (
        <Card extra={`p-6 ${cardStyle}`}>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-center justify-between rounded-2xl bg-gray-50 p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="h-9 w-9 rounded-lg bg-gray-200" />
                  <div className="space-y-2">
                    <div className="h-4 w-40 rounded bg-gray-200" />
                    <div className="h-3 w-60 rounded bg-gray-200" />
                  </div>
                </div>
                <div className="h-3 w-24 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card extra={`p-6 ${cardStyle}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono">
            <thead>
              <tr className="border-b border-gray-200 text-[10px] uppercase text-gray-500">
                <th className="px-3 pb-4">PERSONNEL</th>
                <th className="px-3 pb-4">TYPE</th>
                <th className="px-3 pb-4">REASON</th>
                <th className="px-3 pb-4">PERIOD</th>
                <th className="px-3 pb-4 text-center">DAYS</th>
                <th className="px-3 pb-4">DOCS</th>
                <th className="px-3 pb-4 text-right print:hidden">
                  AUTHORIZATION
                </th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredRequests.map((req) => (
                <tr
                  key={req.id}
                  className="group border-b border-gray-100 transition-all hover:bg-gray-50"
                >
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-3">
                      <SafeAvatar
                        url={req.employee?.avatar_url}
                        wrapperClassName="h-10 w-10"
                        imageClassName="h-full w-full rounded-xl border border-gray-200 shadow-sm"
                      />
                      <div>
                        <p className="text-[13px] uppercase leading-none text-core-charcoal font-medium">
                          {req.employee?.full_name}
                        </p>
                        <p className="mt-1 text-[10px] tracking-widest text-core-accent">
                          #{req.employee?.employee_number}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <span
                      className={`rounded-xl px-2.5 py-1 text-[10px] uppercase font-medium ${
                        req.leave_type.includes("Annual")
                          ? "bg-blue-50 text-blue-600"
                          : "bg-purple-50 text-purple-600"
                      }`}
                    >
                      {req.leave_type}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <p className="max-w-[220px] truncate text-[10px] text-gray-500 font-medium">
                      {req.reason || "---"}
                    </p>
                  </td>
                  <td className="px-3 py-4">
                    <p className="leading-none text-core-charcoal font-medium">
                      {formatDate(req.start_date)}
                    </p>
                    <p className="mt-1 text-[10px] uppercase text-gray-400">
                      UNTIL {formatDate(req.end_date)}
                    </p>
                    <p className="mt-1 text-[10px] uppercase text-gray-400">
                      RECEIVED {formatDateTime(req.created_at)}
                    </p>
                  </td>
                  <td className="px-3 py-4 text-center text-core-charcoal font-medium">
                    {req.days_count}D
                  </td>
                  <td className="px-3 py-4">
                    {req.document_url ? (
                      <button
                        onClick={() => window.open(req.document_url, "_blank")}
                        className="text-core-accent transition-transform hover:scale-110"
                      >
                        <MdAttachFile size={18} />
                      </button>
                    ) : (
                      <span className="text-[9px] italic text-gray-400">
                        NULL
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-right print:hidden">
                    {req.status === "pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(req)}
                          disabled={processingId === req.id}
                          className="bg-emerald-50 text-emerald-600 hover:bg-emerald-600 rounded-lg p-2 transition-all hover:text-white"
                        >
                          <MdCheckCircle size={16} />
                        </button>
                        <button
                          onClick={() => openRejectModal(req)}
                          disabled={processingId === req.id}
                          className="rounded-lg bg-red-50 p-2 text-red-600 transition-all hover:bg-red-600 hover:text-white"
                        >
                          <MdCancel size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(req.id)}
                          className="rounded-lg bg-gray-100 p-2 text-gray-500 transition-all hover:bg-red-600 hover:text-white"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-3">
                        <div className="flex flex-col items-end">
                          <span
                            className={`text-[10px] uppercase font-medium ${
                              req.status === "approved"
                                ? "text-emerald-600"
                                : req.status === "cancelled"
                                ? "text-orange-600"
                                : req.status === "rejected"
                                ? "text-red-600"
                                : "text-gray-400"
                            }`}
                          >
                            {req.status}
                          </span>
                          <span className="mt-1 text-[9px] uppercase text-gray-400">
                            MANAGER: {req.approved_by || ""}
                            {req.approver_number
                              ? ` • #${req.approver_number}`
                              : ""}
                          </span>
                          {req.status === "rejected" && req.rejection_reason ? (
                            <span className="text-red-400 mt-0.5 text-[9px] uppercase">
                              REASON: {req.rejection_reason}
                            </span>
                          ) : null}
                        </div>
                        <button
                          onClick={() => handleDelete(req.id)}
                          className="p-1.5 text-gray-400 transition-colors hover:text-red-500"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-[10px] uppercase text-gray-600 transition-all hover:bg-white hover:text-core-accent hover:border-core-accent"
            >
              {loading ? "LOADING..." : "LOAD_MORE"}
            </button>
          </div>
        )}
      </Card>

      {/* Rejection Modal */}
      {showModal && (
        <div className="bg-core-charcoal/50 fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-md">
          <Card
            extra={`p-8 max-w-md w-full bg-white border border-gray-200 shadow-2xl animate-zoomIn`}
          >
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-mono text-sm font-medium uppercase tracking-widest text-core-charcoal">
                REJECT_PROCEDURE
              </h3>
              <MdCancel className="text-red-500" size={20} />
            </div>
            <textarea
              className="mb-6 h-32 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-core-charcoal outline-none focus:border-red-500 focus:bg-white transition-all"
              placeholder="LOG_REJECTION_REASON..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 rounded-xl bg-gray-100 py-3 text-[10px] font-medium uppercase text-gray-500 transition-all hover:bg-gray-200"
              >
                ABORT
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="flex-1 rounded-xl bg-red-600 py-3 text-[10px] font-medium uppercase text-white shadow-lg shadow-red-600/20 disabled:opacity-30 hover:bg-red-700"
              >
                EXECUTE_REJECTION
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeaveRequestsManagement;